import React, { useEffect, useMemo, useState } from "react";
import { Loader2, X } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../store/hook";
import {
  clearError,
  clearPermissionViews,
  createAuthorizationUser,
  createPermission,
  createResource,
  createRoleResource,
  createRoleResourcePermission,
  createVmsRole,
  deletePermission,
  deleteResource,
  deleteRoleResource,
  deleteRoleResourcePermission,
  deleteVmsRole,
  fetchAllotablePermissions,
  fetchRoleResourcePermissionViews,
  initializeRbac,
  updateAuthorizationUser,
  updatePermission,
  updateResource,
  updateRoleResource,
  updateRoleResourcePermission,
  updateVmsRole,
} from "../../features/rbac/RBACSlice";
import {
  buildTabConfig,
  RBACPageHeader,
  RBACTabNav,
  type RBACTabKey,
} from "./RBACManagementHeader";
import { MappingsTab } from "./tabs/MappingsTab";
import { PermissionsTab } from "./tabs/PermissionsTab";
import { ResourcesTab } from "./tabs/ResourcesTab";
import { RoleResourcesTab } from "./tabs/RoleResourcesTab";
import { RolesTab } from "./tabs/RolesTab";
import { UsersTab } from "./tabs/UsersTab";
import { RBACToasts } from "./shared/RBACToasts";
import { RbacHierarchyFlow } from "./RbacHierarchyFlow";
import { MyAccessCard } from "./MyAccessCard";

const RBACManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState<RBACTabKey>("hierarchy");

  const {
    vmsRoles,
    permissions,
    resources,
    roleResources,
    roleResourcePermissions,
    users,
    permissionViews,
    allotablePermissions,
    loading,
    viewsLoading,
    error,
  } = useAppSelector((state) => state.rbac);

  useEffect(() => {
    dispatch(initializeRbac());
    return () => {
      dispatch(clearPermissionViews());
    };
  }, [dispatch]);

  const tabs = useMemo(
    () =>
      buildTabConfig({
        roles: vmsRoles.length,
        permissions: permissions.length,
        resources: resources.length,
        roleResources: roleResources.length,
        mappings: roleResourcePermissions.length,
        users: users.length,
      }),
    [vmsRoles, permissions, resources, roleResources, roleResourcePermissions, users]
  );

  const handleRefresh = () => {
    dispatch(initializeRbac());
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <RBACPageHeader loading={loading} onRefresh={handleRefresh} />

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {loading && (
          <div className="mb-4 flex items-center gap-2 rounded-md border border-cardborder bg-white px-4 py-3 text-sm text-gray-700">
            <Loader2 className="h-4 w-4 animate-spin text-brand" />
            Loading RBAC data...
          </div>
        )}

        {error && (
          <div className="mb-4 flex items-start gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            <span className="flex-1">{error}</span>
            <button
              type="button"
              onClick={() => dispatch(clearError())}
              className="rounded p-1 text-red-500 hover:bg-red-100"
              aria-label="Dismiss error"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        <div className="mb-6">
          <MyAccessCard />
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[260px_1fr]">
          <aside className="lg:sticky lg:top-6 lg:self-start">
            <div className="rounded-md border border-cardborder bg-white p-3">
              <RBACTabNav active={activeTab} tabs={tabs} onChange={setActiveTab} />
            </div>
          </aside>

          <section className="space-y-6">
            {activeTab === "hierarchy" && (
              <RbacHierarchyFlow
                vmsRoles={vmsRoles}
                roleResources={roleResources}
                roleResourcePermissions={roleResourcePermissions}
                resources={resources}
                permissions={permissions}
                onCreateMapping={(payload) => dispatch(createRoleResourcePermission(payload))}
                onDeleteMapping={(id) => dispatch(deleteRoleResourcePermission(id))}
                onOpenAdvancedMappings={() => setActiveTab("mappings")}
              />
            )}

            {activeTab === "roles" && (
              <RolesTab
                roles={vmsRoles}
                onCreate={(payload) => dispatch(createVmsRole(payload))}
                onUpdate={(payload) => dispatch(updateVmsRole(payload))}
                onDelete={(roleId) => dispatch(deleteVmsRole(roleId))}
              />
            )}

            {activeTab === "permissions" && (
              <PermissionsTab
                permissions={permissions}
                onCreate={(payload) => dispatch(createPermission(payload))}
                onUpdate={(payload) => dispatch(updatePermission(payload))}
                onDelete={(permissionId) => dispatch(deletePermission(permissionId))}
              />
            )}

            {activeTab === "resources" && (
              <ResourcesTab
                resources={resources}
                vmsRoles={vmsRoles}
                onCreate={(payload) => dispatch(createResource(payload))}
                onUpdate={(payload) => dispatch(updateResource(payload))}
                onDelete={(resourceId) => dispatch(deleteResource(resourceId))}
              />
            )}

            {activeTab === "roleResources" && (
              <RoleResourcesTab
                roleResources={roleResources}
                vmsRoles={vmsRoles}
                resources={resources}
                onCreate={(payload) => dispatch(createRoleResource(payload))}
                onUpdate={(payload) => dispatch(updateRoleResource(payload))}
                onDelete={(roleResourceId) => dispatch(deleteRoleResource(roleResourceId))}
              />
            )}

            {activeTab === "mappings" && (
              <MappingsTab
                mappings={roleResourcePermissions}
                roleResources={roleResources}
                resources={resources}
                permissions={permissions}
                permissionViews={permissionViews}
                allotablePermissions={allotablePermissions}
                viewsLoading={viewsLoading}
                onCreate={(payload) => dispatch(createRoleResourcePermission(payload))}
                onUpdate={(payload) => dispatch(updateRoleResourcePermission(payload))}
                onDelete={(id) => dispatch(deleteRoleResourcePermission(id))}
                onFetchPermissionViews={(roleResourceId) =>
                  dispatch(fetchRoleResourcePermissionViews(roleResourceId))
                }
                onFetchAllotable={(resourceId) =>
                  dispatch(fetchAllotablePermissions(resourceId))
                }
              />
            )}

            {activeTab === "users" && (
              <UsersTab
                users={users}
                roleResources={roleResources}
                onCreate={(payload) => dispatch(createAuthorizationUser(payload))}
                onUpdate={(payload) => dispatch(updateAuthorizationUser(payload))}
              />
            )}
          </section>
        </div>
      </main>

      <RBACToasts />
    </div>
  );
};

export default RBACManagement;
